import { useEffect, useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const aboutImages = [
  {
    imageUrl: 'https://readdy.ai/api/search-image?query=Cozy%20modern%20creative%20studio%20workspace%20with%20warm%20golden%20amber%20lighting%2C%20gaming%20posters%20on%20wall%2C%20wooden%20desk%20with%20computer%20setup%2C%20anime%20figurines%20on%20shelf%2C%20warm%20cream%20walls%2C%20editorial%20photography%20style%20with%20soft%20natural%20lighting%2C%20cozy%20atmosphere&width=600&height=400&seq=about-brand-01&orientation=landscape',
    alt: 'Galgame资源大全团队工作环境',
    title: 'Galgame资源大全品牌介绍',
  },
  {
    imageUrl: 'https://readdy.ai/api/search-image?query=Collaborative%20creative%20team%20working%20on%20content%20together%2C%20warm%20golden%20amber%20studio%20lighting%2C%20multiple%20screens%20displaying%20game%20artwork%2C%20friendly%20atmosphere%2C%20modern%20minimalist%20office%20with%20warm%20cream%20walls%2C%20editorial%20photography%20style%2C%20soft%20shadows&width=600&height=400&seq=about-team-01&orientation=landscape',
    alt: 'Galgame资源大全编辑团队',
    title: 'Galgame资源大全团队口碑',
  },
];

const featureItems = [
  {
    icon: 'ri-database-2-line',
    title: '海量资源库',
    desc: '收录超过450款galgame资源信息，涵盖经典名作与最新热门作品',
  },
  {
    icon: 'ri-translate',
    title: '汉化补丁导航',
    desc: '整理各大知名汉化组最新补丁动态，提供一站式汉化资源指引',
  },
  {
    icon: 'ri-article-line',
    title: '深度评测内容',
    desc: '专业编辑团队撰写深度评测与攻略，帮助玩家快速了解游戏亮点',
  },
  {
    icon: 'ri-guide-line',
    title: '新手入门指南',
    desc: '面向galgame新手玩家的完整入门教程，从零开始无障碍入坑',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新维护',
    desc: '编辑团队每周至少更新2-3篇高质量内容，保持信息的时效性',
  },
  {
    icon: 'ri-group-line',
    title: '社区互动交流',
    desc: '建立galgame爱好者社区，分享游戏体验和资源信息',
  },
];

const reviews = [
  {
    name: '小明',
    role: 'galgame爱好者',
    content: 'Galgame资源大全是我找galgame资源的首选网站，分类清晰，汉化补丁信息也很及时。作为新手的时候就是靠这里的入门指南入坑的！',
    rating: 5,
  },
  {
    name: '阿花',
    role: 'ACG文化研究者',
    content: '网站的内容质量很高，评测文章写得很专业。Key社作品回顾那篇文章让我对galgame行业有了更深的了解，非常推荐。',
    rating: 5,
  },
  {
    name: '老王',
    role: '资深玩家',
    content: '从《CLANNAD》时代就开始玩galgame了，这个网站的资源整理做得很到位，汉化补丁的更新也很及时，值得收藏。',
    rating: 5,
  },
  {
    name: '小樱',
    role: '游戏编辑',
    content: '作为同行，我真心觉得这个网站的内容质量很高。文章结构清晰，分类合理，是galgame资源领域做得最好的中文网站之一。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9qoc2ue3oq4jqumtnsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        setFormStatus('error');
        setFormError('服务器响应异常，请稍后重试');
        return;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 · Galgame资源大全 | 品牌介绍与联系方式"
        description="Galgame资源大全是最全面的中文galgame资源聚合平台，致力于为galgame爱好者提供资源下载导航、汉化补丁、推荐合集与新手入门指南。了解我们的品牌故事与团队。"
        keywords="关于Galgame资源大全,galgame资源网站介绍,galgame资源平台,联系我们"
        canonicalUrl="https://fuweitongxin.cn/about"
      />
      <SchemaOrg
        type="Organization"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Galgame资源大全',
          url: 'https://fuweitongxin.cn',
          description: '最全面的中文galgame资源聚合平台',
          foundingDate: '2023',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'contact@fuweitongxin.cn',
            availableLanguage: ['Chinese'],
          },
        }}
      />

      <Navbar />
      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-6">
                  关于 Galgame资源大全
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  Galgame资源大全成立于2023年，是中文互联网上最全面的galgame资源聚合平台之一。我们致力于为galgame爱好者打造一站式的资源查找体验，帮助玩家快速找到心仪的作品和相关信息。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  从经典名作到最新热门，从汉化补丁到深度评测，我们覆盖了galgame玩家可能需要的各类信息。我们的编辑团队由资深galgame玩家和专业内容创作者组成，确保每一篇文章都经过精心打磨。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的使命是让更多人了解和享受galgame这一独特的游戏类型，为广大中文玩家提供最优质的galgame资源导航服务。
                </p>
              </div>
              <div className="flex-1">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={aboutImages[0].imageUrl}
                    alt={aboutImages[0].alt}
                    title={aboutImages[0].title}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                我们的优势
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                六大核心优势，打造最好的galgame资源平台
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featureItems.map((item) => (
                <div
                  key={item.title}
                  className="bg-background-50 rounded-xl p-6 hover:bg-background-200 transition-colors duration-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-2 whitespace-nowrap">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                来自galgame爱好者们的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {reviews.map((review) => (
                <div
                  key={review.name}
                  className="bg-background-100 rounded-xl p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-primary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-heading text-sm font-bold">
                      {review.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100" id="contact">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题或建议？欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2 whitespace-nowrap">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@fuweitongxin.cn</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-weibo-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2 whitespace-nowrap">微博</h3>
                <p className="text-xs text-foreground-500">@Galgame资源大全</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-bilibili-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2 whitespace-nowrap">Bilibili</h3>
                <p className="text-xs text-foreground-500">@Galgame资源大全</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-8">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6 text-center">
                发送消息
              </h3>
              <form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit}
                data-readdy-form=""
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-350 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-350 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-350 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    消息内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-350 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您想说的话（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto self-center px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap text-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      发送中...
                    </>
                  ) : (
                    <>
                      发送消息
                      <i className="ri-send-plane-line ml-2"></i>
                    </>
                  )}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-center text-green-600 font-medium">
                    消息已发送成功，我们会尽快回复您！
                  </p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-center text-red-500 font-medium">
                    {formError || '提交失败，请稍后重试'}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}